<template>
  <div class="space-y-6">
    <!-- Header de la pestaña de Cuentas por Cobrar -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div>
        <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
          <i class="fa fa-hand-holding-usd text-amber-500" />
          <span>Cartera de Cuentas por Cobrar & Deudores</span>
        </h2>
        <p class="text-xs text-gray-500">Saldos pendientes de cobro a clientes para gestión de cobranzas y liquidez.</p>
      </div>

      <div class="flex items-center gap-2">
        <a
          :href="getPdfUrl('receivables')"
          target="_blank"
          class="px-3.5 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
        >
          <i class="fa fa-file-pdf text-rose-600" />
          <span>Exportar Deudores PDF</span>
        </a>
      </div>
    </div>

    <!-- KPIs de Cartera -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center justify-between">
        <div>
          <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Total Saldo por Cobrar</div>
          <div class="text-2xl font-black text-rose-600 mt-1">
            {{ formatCurrency(receivables.total_receivable) }}
          </div>
          <div class="text-[11px] text-gray-500 mt-1">Dinero pendiente en la calle</div>
        </div>
        <div class="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
          <i class="fa fa-money-check-alt text-xl" />
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center justify-between">
        <div>
          <div class="text-xs font-bold uppercase tracking-wider text-gray-400">Clientes con Deuda</div>
          <div class="text-2xl font-black text-gray-900 mt-1">
            {{ receivables.debtors_count }}
          </div>
          <div class="text-[11px] text-gray-500 mt-1">Clientes con saldo deudor mayor a $0</div>
        </div>
        <div class="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
          <i class="fa fa-users text-xl" />
        </div>
      </div>
    </div>

    <!-- Tabla: Top Clientes Deudores -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fa fa-exclamation-triangle text-rose-500" />
          <h3 class="text-sm font-bold text-gray-900">Ranking de Clientes con Mayor Deuda</h3>
        </div>
        <span class="text-xs text-gray-400">
          Ordenado de mayor a menor saldo
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4 text-center" style="width: 5%;">#</th>
              <th class="py-3 px-4" style="width: 35%;">Cliente / Razón Social</th>
              <th class="py-3 px-3">Identificación / RIF</th>
              <th class="py-3 px-3">Teléfono</th>
              <th class="py-3 px-4 text-right">Saldo Deudor ($)</th>
              <th class="py-3 px-4 text-center" style="width: 10%;">Acción</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(client, idx) in receivables.top_debtors"
              :key="client.id"
              class="hover:bg-rose-50/20 transition-colors"
            >
              <td class="py-3 px-4 text-center font-bold text-gray-400">
                {{ idx + 1 }}
              </td>
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ client.name }}</div>
                <div v-if="client.company_name && client.company_name !== client.name" class="text-[10px] text-gray-400">
                  {{ client.company_name }}
                </div>
              </td>
              <td class="py-3 px-3 font-mono text-gray-500">
                {{ client.identification || '-' }}
              </td>
              <td class="py-3 px-3 text-gray-600">
                {{ client.phone || '-' }}
              </td>
              <td class="py-3 px-4 text-right font-mono font-black text-rose-600 text-sm">
                {{ formatCurrency(client.balance) }}
              </td>
              <td class="py-3 px-4 text-center">
                <a
                  :href="`/clients/${client.id}`"
                  target="_blank"
                  class="px-2.5 py-1 bg-white border border-gray-200 hover:border-orange-300 hover:text-orange-600 rounded-lg text-[11px] font-semibold transition-colors inline-flex items-center gap-1 shadow-xs"
                >
                  <span>Ver Ficha</span>
                  <i class="fa fa-external-link-alt text-[9px]" />
                </a>
              </td>
            </tr>
            <tr v-if="!receivables.top_debtors?.length">
              <td colspan="6" class="text-center py-8 text-emerald-600 font-medium">
                <i class="fa fa-check-circle text-2xl text-emerald-500 mb-2 block" />
                <span>¡Excelente! No hay clientes con cuentas por cobrar pendientes.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReceivablesTab',

  props: {
    receivables: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getPdfUrl(type) {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
